import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDecoder, useSessionContext } from "../../../../hooks";
import { DecoderResponseModel, VehicleDataDecoder } from "../../../../models";
import { ModalDecoderMolecule } from "../modal-decoder/modal-decoder.molecule";
import logoMolicar from "./../../../assets/images/logo-molicar-white.png";

import {
  BoxSearch,
  ConsultButton,
  ContainerResultVehicleDecoder,
  ContainerSearchVehicle,
  Divider,
  Field,
  FieldInput,
  FieldInputWithIcon,
  FieldLabel,
  Flag,
  FlagBrazil,
  PlateBottom,
  PlateInput,
  PlateInputWrapper,
  PlateTop,
  RightIcon,
  TitleSearch,
  VinFieldWrapper,
  VinInput,
  WrapperSearchVehicleDecoder,
  ActionsRow,
  HeaderContainer,
  LogoutButton,
} from "./search-vehicle-decoder.molecule.styled";
import { useHistory } from "react-router-dom";

export const SearchVehicleDecoderMolecule = (): JSX.Element => {
  const {
    tokenDecoder,
    setTokenDecoder,
    clientId,
    clientSecret,
    setClientId,
    setClientSecret,
  } = useSessionContext();
  const { postFindByPlate, postAuthToken, postFindByVin } = useDecoder();
  const { t } = useTranslation();
  const history = useHistory();

  const [plate, setPlate] = useState("");
  const [vin, setVin] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState<VehicleDataDecoder | null>(
    null
  );
  const [open, setOpen] = useState(false);

  const normalizePlate = (raw: string) =>
    raw
      .toUpperCase()
      .replace(/-/g, "")
      .replace(/[^A-Z0-9]/g, "");
  const isValidPlate = (raw: string) =>
    /^[A-Z0-9]{7}$/.test(normalizePlate(raw));

  const normalizeVin = (raw: string) =>
    raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const isValidVin = (raw: string) =>
    /^[A-HJ-NPR-Z0-9]{17}$/.test(normalizeVin(raw));

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    value = value.toUpperCase().slice(0, 7);
    if (value.length > 3) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    }
    setPlate(value);
  };

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    value = value.replace(/[IOQ]/g, "").slice(0, 17);
    setVin(value);
  };

  const activeField = useMemo<"vin" | "plate" | null>(() => {
    const hasVin = vin.trim().length > 0;
    const hasPlate = plate.trim().length > 0;
    if (hasVin) return "vin";
    if (hasPlate) return "plate";
    return null;
  }, [plate, vin]);

  const disabledPlate = activeField === "vin";
  const disabledVin = activeField === "plate";

  const canConsult = useMemo(() => {
    if (activeField === "plate") return isValidPlate(plate);
    if (activeField === "vin") return isValidVin(vin);
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField, plate, vin]);

  useEffect(() => {
    const authenticate = async () => {
      try {
        const token = await postAuthToken(clientId, clientSecret);
        setTokenDecoder(token.access_token);
      } catch (err: any) {
        console.error("Erro na autenticação:", err);
      }
    };
    authenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConsult = async () => {
    setVehicleData(null);

    if (!tokenDecoder) {
      return;
    }
    if (!activeField || !canConsult) {
      return;
    }

    setLoading(true);
    try {
      let apiData: any;
      if (activeField === "plate") {
        const normalized = normalizePlate(plate);
        apiData = await postFindByPlate(normalized, tokenDecoder);
      } else {
        const normalized = normalizeVin(vin);
        apiData = await postFindByVin(normalized, tokenDecoder);
      }
      const mapped = mapApiToVehicleData(apiData);
      setVehicleData(mapped);
    } catch (err: any) {
      console.error("Erro na consulta:", err);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConsult = () => {
    setPlate("");
    setVin("");
    setVehicleData(null);
    setOpen(false);
  };

  const handleLogout = () => {
    try {
      setTokenDecoder(null);
      setClientId("");
      setClientSecret("");
    } catch {
      setTokenDecoder("");
    }

    setPlate("");
    setVin("");
    setVehicleData(null);
    setOpen(false);

    history.push(`/decoder`);
  };

  function mapApiToVehicleData(value: any): VehicleDataDecoder {
    const valuesVehicle: DecoderResponseModel = value?.data ?? value ?? {};

    const valueDecoder = valuesVehicle.Decoder ?? {};
    const vehicleData = valuesVehicle.VehicleData ?? {};
    const pricing = valuesVehicle.Pricing ?? {};
    const restrictions = valuesVehicle.Restrictions ?? {};

    return {
      plate: valueDecoder.Plate ?? "",
      vin: valueDecoder.Vin ?? "",
      yearModel: valueDecoder.ModelYear ?? "",
      status: valueDecoder.Status ?? "",
      molicarId: valueDecoder.MolicarId ?? "",

      molicarPrice: pricing.MolicarPrice ?? "",

      axels: vehicleData.Axles ?? "",
      bodyType: vehicleData.BodyType ?? "",
      cc: vehicleData.CC ?? "",
      color: vehicleData.VehicleColor ?? "",
      vehicleSpec: vehicleData.VehicleSpecie ?? "",
      vehicleType: vehicleData.VehicleType ?? "",
      manufactureYear: vehicleData.ManufacturedYear ?? "",
      maxTractionCapacity: vehicleData.MaximumTractionCapacity ?? "",
      nationality: vehicleData.Nationality ?? "",
      ufOrigin: vehicleData.UFOrigin ?? "",
      ufCurrent: vehicleData.UFCurrent ?? "",
      cityCurrent: vehicleData.CurrentCityName ?? "",
      engineNumber: vehicleData.EngineNumber ?? "",
      size: vehicleData.Size ?? "",
      brand: vehicleData.Brand ?? "",
      model: vehicleData.Model ?? "",
      version: vehicleData.Version ?? "",
      doors: vehicleData.NrDoors ?? "",
      seats: vehicleData.NrSeats ?? "",
      traction: vehicleData.DriveTrain ?? "",
      grossWeight: vehicleData.TotalGrossWeight ?? "",
      weightCarriage: vehicleData.WeightCarriage ?? "",
      fuelType: vehicleData.FuelType ?? "",
      transmission: vehicleData.Transmission ?? "",
      turbo: vehicleData.HasTurbo ?? "",
      adas: vehicleData.ADAS ?? "",

      restrictionCode01: restrictions.RestrictionCode01 ?? null,
      restrictionCode02: restrictions.RestrictionCode02 ?? null,
      restrictionCode03: restrictions.RestrictionCode03 ?? null,
      restrictionCode04: restrictions.RestrictionCode04 ?? null,
    };
  }

  return (
    <>
      <WrapperSearchVehicleDecoder>
        <HeaderContainer>
          <img src={logoMolicar} alt="Logo Molicar" />
          <LogoutButton type="button" onClick={handleLogout}>
            Sair
          </LogoutButton>
        </HeaderContainer>

        <ContainerSearchVehicle>
          <TitleSearch>Buscar veículo</TitleSearch>

          <BoxSearch>
            <div>
              <PlateInputWrapper>
                <PlateTop>
                  <FlagBrazil>BRASIL</FlagBrazil>
                  <Flag />
                </PlateTop>
                <PlateBottom>
                  <PlateInput
                    placeholder="Digite aqui"
                    type="text"
                    value={plate}
                    onChange={handlePlateChange}
                    disabled={!tokenDecoder || disabledPlate}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        activeField === "plate" &&
                        canConsult &&
                        !loading
                      ) {
                        handleConsult();
                      }
                    }}
                  />
                </PlateBottom>
              </PlateInputWrapper>
            </div>

            <span>ou</span>

            <div>
              <VinFieldWrapper>
                <VinInput
                  id="vin-input"
                  placeholder="Chassi"
                  type="text"
                  value={vin}
                  onChange={handleVinChange}
                  disabled={!tokenDecoder || disabledVin}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      activeField === "vin" &&
                      canConsult &&
                      !loading
                    ) {
                      handleConsult();
                    }
                  }}
                />
              </VinFieldWrapper>
            </div>

            <div>
              <ConsultButton
                type="button"
                onClick={handleConsult}
                disabled={
                  !activeField || !canConsult || loading || !tokenDecoder
                }
              >
                {loading ? "Consultando..." : "Consultar"}
              </ConsultButton>
            </div>
          </BoxSearch>
        </ContainerSearchVehicle>

        <ContainerResultVehicleDecoder>
          <Field>
            <FieldLabel htmlFor="res-placa">{t("Plate")}</FieldLabel>
            <FieldInput
              id="res-placa"
              value={vehicleData?.plate ?? ""}
              placeholder={t("Plate")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="res-vin">{t("Vin")}</FieldLabel>
            <FieldInput
              id="res-vin"
              value={vehicleData?.vin ?? ""}
              placeholder={t("Vin")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="res-ano-modelo">{t("ModelYear")}</FieldLabel>
            <FieldInput
              id="res-ano-modelo"
              value={vehicleData?.yearModel ?? ""}
              placeholder={t("ModelYear")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="res-status">Status</FieldLabel>
            <FieldInput
              id="res-status"
              value={vehicleData?.status ?? ""}
              placeholder="0"
              disabled
            />
          </Field>

          <Field span={2}>
            <FieldLabel htmlFor="res-molicar-id">{t("MolicarId")}</FieldLabel>
            <FieldInputWithIcon
              id="res-molicar-id"
              value={vehicleData?.molicarId ?? ""}
              placeholder={t("MolicarId")}
              disabled
            />
            <RightIcon>▾</RightIcon>
          </Field>

          <Divider />

          <Field>
            <FieldLabel>{t("Axles")}</FieldLabel>
            <FieldInput
              value={vehicleData?.axels ?? ""}
              placeholder={t("Axles")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("BodyType")}</FieldLabel>
            <FieldInput
              value={vehicleData?.bodyType ?? ""}
              placeholder={t("BodyType")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("CC")}</FieldLabel>
            <FieldInput
              value={vehicleData?.cc ?? ""}
              placeholder={t("CC")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("VehicleColor")}</FieldLabel>
            <FieldInput
              value={vehicleData?.color ?? ""}
              placeholder={t("VehicleColor")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("VehicleSpec")}</FieldLabel>
            <FieldInput
              value={vehicleData?.vehicleSpec ?? ""}
              placeholder={t("VehicleSpec")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("VehicleType")}</FieldLabel>
            <FieldInput
              value={vehicleData?.vehicleType ?? ""}
              placeholder={t("VehicleType")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("YearOfManufacture")}</FieldLabel>
            <FieldInput
              value={vehicleData?.manufactureYear ?? ""}
              placeholder={t("YearOfManufacture")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("MaximumTractionCapacity")}</FieldLabel>
            <FieldInput
              value={vehicleData?.maxTractionCapacity ?? ""}
              placeholder={t("MaximumTractionCapacity")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Nationality")}</FieldLabel>
            <FieldInput
              value={vehicleData?.nationality ?? ""}
              placeholder={t("Nationality")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("OriginUF")}</FieldLabel>
            <FieldInput
              value={vehicleData?.ufOrigin ?? ""}
              placeholder={t("OriginUF")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("CurrentUF")}</FieldLabel>
            <FieldInput
              value={vehicleData?.ufCurrent ?? ""}
              placeholder={t("CurrentUF")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("CurrentCity")}</FieldLabel>
            <FieldInput
              value={vehicleData?.cityCurrent ?? ""}
              placeholder={t("CurrentCity")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("EngineNumber")}</FieldLabel>
            <FieldInput
              value={vehicleData?.engineNumber ?? ""}
              placeholder={t("EngineNumber")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Size")}</FieldLabel>
            <FieldInput
              value={vehicleData?.size ?? ""}
              placeholder={t("Size")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Brand")}</FieldLabel>
            <FieldInput
              value={vehicleData?.brand ?? ""}
              placeholder={t("Brand")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Model")}</FieldLabel>
            <FieldInput
              value={vehicleData?.model ?? ""}
              placeholder={t("Model")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Version")}</FieldLabel>
            <FieldInput
              value={vehicleData?.version ?? ""}
              placeholder={t("Version")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>Nº de {t("Doors")}</FieldLabel>
            <FieldInput
              value={vehicleData?.doors ?? ""}
              placeholder={t("Doors")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>Nº de {t("Places")}</FieldLabel>
            <FieldInput
              value={vehicleData?.seats ?? ""}
              placeholder={t("Places")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Traction")}</FieldLabel>
            <FieldInput
              value={vehicleData?.traction ?? ""}
              placeholder={t("Traction")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("WeightTotal")}</FieldLabel>
            <FieldInput
              value={vehicleData?.grossWeight ?? ""}
              placeholder={t("Weight")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("WeightBody")}</FieldLabel>
            <FieldInput
              value={vehicleData?.weightCarriage ?? ""}
              placeholder={t("WeightBody")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("FuelType")}</FieldLabel>
            <FieldInput
              value={vehicleData?.fuelType ?? ""}
              placeholder={t("FuelType")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Transmission")}</FieldLabel>
            <FieldInput
              value={vehicleData?.transmission ?? ""}
              placeholder={t("Transmission")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>{t("Turbo")}</FieldLabel>
            <FieldInput
              value={
                vehicleData?.turbo !== undefined
                  ? typeof vehicleData?.turbo === "boolean"
                    ? vehicleData?.turbo
                      ? "Sim"
                      : "Não"
                    : String(vehicleData?.turbo)
                  : ""
              }
              placeholder={t("Turbo")}
              disabled
            />
          </Field>

          <Field>
            <FieldLabel>ADAS</FieldLabel>
            <FieldInput
              value={vehicleData?.adas ?? ""}
              placeholder="ND"
              disabled
            />
          </Field>

          <Divider />

          <Field span={1}>
            <FieldLabel>{t("PriceMolicar")}</FieldLabel>
            <FieldInput
              value={vehicleData?.molicarPrice ?? ""}
              placeholder={t("PriceMolicar")}
              disabled
            />
          </Field>

          <Divider />

          <ActionsRow>
            <ConsultButton
              type="button"
              onClick={handleNewConsult}
              disabled={!vehicleData || loading}
            >
              Nova Consulta
            </ConsultButton>
          </ActionsRow>
        </ContainerResultVehicleDecoder>
      </WrapperSearchVehicleDecoder>

      <ModalDecoderMolecule
        open={open}
        onClose={() => setOpen(false)}
        title="Dados não conferem"
        description="Parece que algo não está certo na placa ou no chassi. Confira os dados e tente novamente."
        confirmLabel="Revisar informações"
      />
    </>
  );
};
